import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, Text, Icon } from '@/ui-kits';
import { Profile } from '@/ui-kits/Profile';
import { getPatientDetailsRequest } from '@/services/redux/slices/specialistDashboardSlice';
import { specialistPatientDetails, specialistPatientDetailsLoadingStatus } from '@/services/redux/selectors';
import { LoaderCircle } from 'lucide-react';
import { Gender } from '@/constants';
import { formatName, ageFromDate } from '@/utils/common_functions';
import { ReportViewerModal } from '../../ReportViewerModal';

interface PatientDetailsModalProps {
  caseId: number;
  isOpen: boolean;
  onClose: () => void;
}

export const PatientDetailsModal: React.FC<PatientDetailsModalProps> = ({ caseId, isOpen, onClose }) => {
  const dispatch = useDispatch();
  const patientDetails = useSelector(specialistPatientDetails);
  const isLoading = useSelector(specialistPatientDetailsLoadingStatus);
  const [isReportViewerOpen, setIsReportViewerOpen] = useState(false);
  const [reportUrl, setReportUrl] = useState('');
  const [reportCaseId, setReportCaseId] = useState(0);

  const getGenderLabel = (genderValue: number): string => {
    switch (genderValue) {
      case Gender.Male:
        return 'Male';
      case Gender.Female:
        return 'Female';
      case Gender.Other:
        return 'Other';
      default:
        return 'N/A';
    }
  };

  const handleViewReport = (case_id: number, report: string) => {
    setReportUrl(report);
    setReportCaseId(case_id);
    setIsReportViewerOpen(true);
  };

  const handleCloseReportViewer = () => {
    setIsReportViewerOpen(false);
    setReportUrl('');
    setReportCaseId(0);
  };

  useEffect(() => {
    if (isOpen && caseId) {
      dispatch(getPatientDetailsRequest({ id: caseId }));
    }
  }, [isOpen, caseId]);

  if (!isOpen) return null;

  return (
    <>
      <Modal
        open={isOpen}
        onClose={onClose}
        className="rounded-2xl p-0 w-[95vw] sm:w-[90vw] md:w-[85vw] lg:w-[75vw] max-w-[920px] max-h-[90vh] bg-white shadow-2xl">
        <div className="flex flex-col h-full max-h-[90vh] bg-white rounded-2xl overflow-hidden">
          {/* Header */}
          <div className="flex-shrink-0 px-5 pt-5 pb-2 bg-white">
            <div className="flex items-center justify-between gap-2">
              <div className="text-lg sm:text-2xl text-ui_blue flex items-baseline gap-1 sm:gap-2 flex-nowrap min-w-0">
                <span className="font-FigtreeSemiBold whitespace-nowrap">Patient Details</span>
                {caseId && (
                  <span className="font-FigtreeMedium text-base sm:text-xl whitespace-nowrap">
                    (Case ID: <span className="font-FigtreeBold text-base sm:text-xl">#{caseId}</span>)
                  </span>
                )}
              </div>
              <button onClick={onClose} className="p-1.5 rounded-full hover:bg-gray-100 transition flex-shrink-0">
                <Icon type="close" className="size-6 text-ui_blue" />
              </button>
            </div>
          </div>

          {/* Body (scrollable) */}
          <div className="flex-1 overflow-y-auto px-5 py-6 patient-details-scroll" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            <style>
              {`
              .patient-details-scroll::-webkit-scrollbar {
                display: none;
              }
            `}
            </style>
            {isLoading ? (
              <div className="flex justify-center items-center py-24">
                <LoaderCircle className="size-12 animate-spin text-ui_blue" />
              </div>
            ) : patientDetails ? (
              <div className="flex flex-col gap-6">
                {/* Patient Profile Card */}
                <div className="rounded-2xl p-6 pt-3 bg-ui_bg_pale_blue border border-ui_input_border text-ui_blue">
                  <Text variant="title" className="font-FigtreeSemiBold text-ui_blue mb-6">
                    Patient Profile
                  </Text>

                  <div className="flex flex-col sm:flex-row gap-6">
                    {/* Photo + Name */}
                    <div className="flex flex-col items-center gap-3 flex-shrink-0 sm:ml-4 md:ml-6">
                      <Profile
                        profile_url={patientDetails.photo_url}
                        username={patientDetails.name}
                        className="size-32 border-2 border-ui_bg_light_blue shadow-md rounded-full"
                        textClassname="text-4xl"
                      />
                      <Text
                        variant="free"
                        className="text-2xl sm:text-2xl font-FigtreeBold text-center bg-gradient-to-r from-ui_blue to-ui_blue_light bg-clip-text text-transparent">
                        {formatName(patientDetails.name)}
                      </Text>
                    </div>

                    {/* Details - all in one line */}
                    <div className="flex-1 flex flex-col gap-4 sm:pl-6 md:pl-8 lg:pl-10">
                      {/* Demographics in one line */}
                      <div className="flex gap-10 lg:gap-24 flex-wrap">
                        <div>
                          <Text className="font-FigtreeMedium text-[11px] uppercase tracking-[0.025em] text-ui_input_border_dark">Gender</Text>
                          <Text className="font-FigtreeSemiBold font-semibold text-base text-ui_blue mt-1">{getGenderLabel(patientDetails.gender)}</Text>
                        </div>
                        <div>
                          <Text className="font-FigtreeMedium text-[11px] uppercase tracking-[0.025em] text-ui_input_border_dark">Age</Text>
                          <Text className="font-FigtreeSemiBold font-semibold text-base text-ui_blue mt-1">
                            {ageFromDate(patientDetails.dob ?? null) ?? 'N/A'}
                          </Text>
                        </div>
                        <div>
                          <Text className="font-FigtreeMedium text-[11px] uppercase tracking-[0.025em] text-ui_input_border_dark">Weight</Text>
                          <Text className="font-FigtreeSemiBold font-semibold text-base text-ui_blue mt-1">
                            {patientDetails.weight ? `${patientDetails.weight} kg` : 'N/A'}
                          </Text>
                        </div>
                        <div>
                          <Text className="font-FigtreeMedium text-lg uppercase tracking-[0.025em] text-ui_input_border_dark">Height</Text>
                          <Text className="font-FigtreeSemiBold font-semibold text-base text-ui_blue mt-1">
                            {patientDetails.height ? `${patientDetails.height} cm` : 'N/A'}
                          </Text>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-24 gap-3">
                <Text className="font-FigtreeMedium text-base text-ui_input_border_dark">No patient details available</Text>
              </div>
            )}
          </div>
        </div>
      </Modal>

      <ReportViewerModal
        isOpen={isReportViewerOpen}
        onClose={handleCloseReportViewer}
        report_url={reportUrl}
        case_id={reportCaseId}
      />
    </>
  );
};
